import { PERMISSION_ACTION_SEPARATOR, PERMISSION_ACTIONS, PERMISSION_SCOPE, PERMISSION_SCOPES, PERMISSION_VERB_REQUIRES, } from './permissions.constants.js';
function isPermissionScope(value) {
    return PERMISSION_SCOPES.includes(value);
}
/**
 * The scope is the last part of `<resource>:<verb>:<scope>`. It is read from the name rather than
 * stored, so there is no second copy that could disagree with it.
 */
export function getPermissionScope(action) {
    const scope = action.split(PERMISSION_ACTION_SEPARATOR).at(-1) ?? '';
    if (!isPermissionScope(scope)) {
        throw new Error(`Permission action "${action}" does not end in a known scope`);
    }
    return scope;
}
/**
 * Whether `user` may perform any of `actions` on `municipalityId`'s data: a global permission
 * works anywhere, a municipality one only in the user's own municipality. The one rule shared by
 * core-api's `restrictTo` and the web apps, so the UI never offers what the API would refuse.
 * `municipalityId` undefined means a route that names no municipality, where only global
 * permissions count.
 */
export function hasPermission(params) {
    const { user, actions, municipalityId } = params;
    return user.role.permissions.some((permission) => {
        if (!actions.includes(permission.action)) {
            return false;
        }
        if (getPermissionScope(permission.action) === PERMISSION_SCOPE.GLOBAL) {
            return true;
        }
        return municipalityId !== undefined && user.municipalityId === municipalityId;
    });
}
/**
 * Whether `user` holds every one of `actions` themselves, so they may hand them out in a role or
 * an invite: nobody can create a coworker or a role more powerful than themselves. A global
 * permission covers its municipality form (`roles:read:global` covers `roles:read:municipality`),
 * which is how internal staff, who hold no municipality permissions, still grant them.
 */
export function holdsAllPermissions(params) {
    const { user, actions } = params;
    const held = new Set(user.role.permissions.map((permission) => permission.action));
    return actions.every((action) => held.has(action) || held.has(toGlobalAction(action)));
}
function toGlobalAction(action) {
    const parts = action.split(PERMISSION_ACTION_SEPARATOR);
    return [...parts.slice(0, -1), PERMISSION_SCOPE.GLOBAL].join(PERMISSION_ACTION_SEPARATOR);
}
/**
 * Splits `<resource>:<verb>:<scope>` so the UI can label a permission by its parts. The parts are
 * typed from PERMISSION_ACTION, so a translation key built from them can't name an unknown one.
 */
export function parsePermissionAction(action) {
    const [resource, verb, ...rest] = action.split(PERMISSION_ACTION_SEPARATOR);
    if (!resource || !verb || rest.length !== 1) {
        throw new Error(`Permission action "${action}" is not <resource>:<verb>:<scope>`);
    }
    return {
        resource: resource,
        verb: verb,
        scope: getPermissionScope(action),
    };
}
/**
 * The actions `action` can't be held without: same resource and scope, a verb it requires. A
 * resource that doesn't offer the required verb (road segment attributes have no read) needs
 * nothing.
 */
export function readRequiredPermissionActions(action) {
    const { resource, verb, scope } = parsePermissionAction(action);
    const requiredVerbs = PERMISSION_VERB_REQUIRES[verb];
    return PERMISSION_ACTIONS.filter((candidate) => {
        const parts = parsePermissionAction(candidate);
        return (parts.resource === resource && parts.scope === scope && requiredVerbs.includes(parts.verb));
    });
}
/** The actions that require `action`, which have to go when it goes. */
export function readDependentPermissionActions(action) {
    return PERMISSION_ACTIONS.filter((candidate) => readRequiredPermissionActions(candidate).includes(action));
}
/** What `actions` require but leave out; empty when the set is valid to hold. */
export function findMissingRequiredPermissions(actions) {
    const held = new Set(actions);
    const required = new Set(actions.flatMap(readRequiredPermissionActions));
    return [...required].filter((action) => !held.has(action));
}
